import React, { useEffect, useState } from "react";
import swal from "sweetalert";
import { API } from "../../constants/env";
import { displayGrade } from "../../utils/grade";

const computeMoyGen = (allC) => {
  const weights = { premier: 1, deuxieme: 1.5, troisieme: 2 };
  let num = 0, den = 0;
  for (const t of ["premier", "deuxieme", "troisieme"]) {
    const m = allC?.[t]?.moyenne;
    if (m !== undefined && !isNaN(m)) {
      num += m * weights[t];
      den += weights[t];
    }
  }
  if (den === 0) return allC?.moyenne ?? 0;
  return num / den;
};

const inputStyle = {
  outline: 0,
  width: 72,
  border: "1px solid #e5e7eb",
  borderRadius: 3,
  padding: "4px 6px",
  fontSize: 13,
  background: "white",
};

const labelStyle = {
  fontSize: 11,
  color: "#6b7280",
  display: "block",
  marginBottom: 3,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const valueTextStyle = {
  fontSize: 22,
  fontWeight: 600,
  color: "#111827",
  fontVariantNumeric: "tabular-nums",
  lineHeight: 1.2,
};

const annualInputBase = {
  outline: 0,
  width: 72,
  border: "1px solid transparent",
  borderRadius: 3,
  padding: "4px 6px",
  fontSize: 22,
  fontWeight: 600,
  color: "#111827",
  background: "transparent",
  fontVariantNumeric: "tabular-nums",
};

const SmoyenneG = ({
  carnetMoyenne,
  studentId,
  classeId,
  minmax,
  isLastStudent,
  saveCarnet,
}) => {
  const [allC, setAllC] = useState({});
  const [computedAvg, setComputedAvg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [annualAvg, setAnnualAvg] = useState(null);

  useEffect(() => {
    if (studentId) {
      fetch(API.overAllAverge.get(studentId), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((res) => res.json())
        .then((response) => {
          const trimestreLocal = localStorage.getItem("tris");
          if (!response) {
            setAllC({ moyenne: 0, max: 0, min: 0, tmax: 0, tmin: 0 });
          } else if (response.error) {
            return swal("Error", response.message, "error");
          } else {
            let tmax = 0, tmin = 0;
            switch (trimestreLocal) {
              case "premier":   tmax = response.premier?.max ?? 0; tmin = response.premier?.min ?? 0; break;
              case "deuxieme":  tmax = response.deuxieme?.max ?? 0; tmin = response.deuxieme?.min ?? 0; break;
              case "troisieme": tmax = response.troisieme?.max ?? 0; tmin = response.troisieme?.min ?? 0; break;
            }
            setAllC({ ...response, tmax, tmin });
          }
        })
        .catch((e) => swal("Error", e.message, "error"));
    }
  }, [studentId]);

  useEffect(() => {
    const semestre = localStorage.getItem("tris");
    if (!classeId || !semestre) { setComputedAvg(null); return; }
    fetch(API.avgMinMax.get(classeId, semestre), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": localStorage.getItem("token"),
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response && !response.error && typeof response.min === "number") {
          setComputedAvg({ min: response.min, max: response.max });
        } else {
          setComputedAvg(null);
        }
      })
      .catch(() => setComputedAvg(null));
  }, [classeId, studentId]);

  useEffect(() => {
    if (!classeId) { setAnnualAvg(null); return; }
    fetch(API.annualMinMax.get(classeId), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": localStorage.getItem("token"),
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response && !response.error && typeof response.min === "number") {
          setAnnualAvg({ min: response.min, max: response.max });
        } else {
          setAnnualAvg(null);
        }
      })
      .catch(() => setAnnualAvg(null));
  }, [classeId, studentId]);

  const saveMoyennes = () => {
    return fetch(API.overAllAverge.update(studentId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": localStorage.getItem("token"),
      },
      body: JSON.stringify({ ...allC, trimestre: localStorage.getItem("tris") }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.error) return Promise.reject(new Error(response.message));
      })
      .catch((e) => swal("Error", e.message, "error"));
  };

  const changeMoyenne = (value, name) => {
    if (value.length > 1 && value.charAt(0) === "0" && value.charAt(1) !== ",")
      value = value.substring(1);
    if (parseFloat(value) > 20) value = 20;
    if (parseFloat(value) < 0) value = 0;
    setAllC({ ...allC, [name]: Number(value) });
  };

  return (
    <div style={{
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: 4,
      padding: "12px 16px",
      marginTop: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 500 }}>
            Moy. trimestrielle
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, color: "#111827", fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>
            {displayGrade(carnetMoyenne)}
          </div>
        </div>

        <div style={{ width: 1, height: 36, background: "#e5e7eb", flexShrink: 0 }} />

        {minmax ? (<>
          <div>
            <label style={labelStyle}>Plus élevée</label>
            <div style={valueTextStyle}>
              {computedAvg ? displayGrade(computedAvg.max) : "—"}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Plus basse</label>
            <div style={valueTextStyle}>
              {computedAvg ? displayGrade(computedAvg.min) : "—"}
            </div>
          </div>
        </>) : (<>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", borderLeft: "1px solid #e5e7eb", paddingLeft: 16 }}>
            <div>
              <label style={labelStyle}>Plus élevée (T3)</label>
              <div style={valueTextStyle}>
                {computedAvg ? displayGrade(computedAvg.max) : "—"}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Plus basse (T3)</label>
              <div style={valueTextStyle}>
                {computedAvg ? displayGrade(computedAvg.min) : "—"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", borderLeft: "1px solid #e5e7eb", paddingLeft: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 500 }}>
                Moyenne annuelle
              </div>
              <div style={{ fontSize: 22, fontWeight: 600, color: "#111827", fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>
                {displayGrade(computeMoyGen(
                (() => {
                  const _tris = localStorage.getItem("tris");
                  const _frais = typeof carnetMoyenne === "number" && !isNaN(carnetMoyenne);
                  return (_tris && _frais)
                    ? { ...allC, [_tris]: { ...(allC?.[_tris] || {}), moyenne: carnetMoyenne } }
                    : allC;
                })()
              ))}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Plus élevée (année)</label>
              <div style={valueTextStyle}>
                {annualAvg ? displayGrade(annualAvg.max) : "—"}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Plus basse (année)</label>
              <div style={valueTextStyle}>
                {annualAvg ? displayGrade(annualAvg.min) : "—"}
              </div>
            </div>
          </div>
        </>)}

        <button
          className="btn btn-success"
          disabled={saving}
          style={{ marginLeft: "auto", padding: "7px 16px", fontSize: 13, whiteSpace: "nowrap", opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          onClick={async () => {
            if (saving) return;
            setSaving(true);
            try {
              await saveMoyennes();
              saveCarnet();
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving
            ? "Enregistrement…"
            : isLastStudent
            ? "Confirmer et terminer ✓"
            : "Confirmer et passer au suivant"}
        </button>
      </div>
    </div>
  );
};

export default SmoyenneG;
